<template>
  <div v-if="showModal" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 museo-sans-normal">
    <div class="bg-white p-6 rounded-md shadow-md w-1/3">
      <div class="flex justify-between pb-2">
        <h1 class="font-bold text-3xl text-center mb-5">Billing Information</h1>
        <button class="text-black font-bold rounded-full border border-s-gray-200 p-1 w-8 h-8 cursor-pointer" @click="$emit('close')">x</button>
      </div>
      <h2 class="text-xl font-bold mb-4">Payment Method</h2>
      <div class="flex w-full flex-col p-6 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-100">
        <div class="flex w-full">
          <h5 class="font-bold mr-auto">Mpesa</h5>
        </div>
        <p class="mt-5">
          Your fixed monthly billing plan requires full settlement of the outstanding amount below to lift or avoid account suspension of other roles.
        </p>
        <ul v-if="outstandingBills.length" class="mt-3 text-sm text-gray-700 list-disc list-inside">
          <li v-for="bill in outstandingBills" :key="bill.month">{{ bill.month }}: KES {{ bill.amount.toFixed(2) }}</li>
        </ul>
        <h5 class="font-bold mt-4 text-sm">Amount Payable (KES)</h5>
        <div class="max-w-xl mt-3">
          <input
            :value="props.amount.toFixed(2)"
            type="text"
            readonly
            class="block p-2.5 w-full text-sm text-gray-900 bg-gray-100 border border-gray-300 rounded-md cursor-not-allowed"
          />
          <p class="text-gray-500 text-xs mt-1">Full settlement is required for fixed monthly billing — this amount is not editable.</p>
        </div>
        <h5 class="font-bold mt-4 text-sm">Mpesa Number</h5>
        <div class="max-w-xl mt-3">
          <div class="flex items-center">
            <div class="shrink-0 z-10 inline-flex items-center py-2.5 px-4 text-sm font-medium text-center text-gray-900 bg-gray-100 border border-gray-300 rounded-s-lg">
              +254
            </div>
            <div class="relative w-full">
              <input
                v-model="phoneNumber"
                type="text"
                class="block p-2.5 w-full z-20 text-sm text-gray-900 bg-gray-50 rounded-e-lg border-s-0 border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g 712 xxx xxx"
                required
              />
            </div>
          </div>
        </div>
        <div class="flex items-center justify-center mt-4 gap-2">
          <button
            @click="proceedToPayment"
            class="px-10 py-2 rounded-md"
            :class="promptingPayment ? 'bg-gray-200 text-black border-custom-blue' : 'bg-custom-blue text-white'"
            :disabled="promptingPayment"
          >
            {{ initialPromptSent ? (promptingPayment ? 'Resending prompt...' : 'Resend Prompt') : (promptingPayment ? 'Sending prompt...' : 'Send Prompt') }}
          </button>
          <button
            v-if="initialPromptSent"
            class="text-white bg-green-600 py-2 px-4"
            @click.prevent="confirmPayment"
            :disabled="confirmingPayment"
          >
            {{ confirmingPayment ? 'Confirming ...' : 'Confirm Payment' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, defineProps } from 'vue'
import apiClient from '@/services/api'
import { useToast } from 'vue-toast-notification'

const props = defineProps({
  amount: Number,
  outstandingBills: { type: Array, default: () => [] },
  show: Boolean,
  facilityId: String
});

const promptingPayment = ref(false)
const initialPromptSent = ref(false)
const confirmingPayment = ref(false)
const phoneNumber = ref('')

const showModal = computed(() => props.show)

const toast = useToast({
  type: 'default',
  position: 'top-right',
  duration: 4000,
  dismissable: true,
});

const formatKenyanPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return 'N/A'

  let cleaned = phoneNumber.toString().replace(/\D/g, '');
  if (cleaned.startsWith('0')) cleaned = '254' + cleaned.slice(1)
  else if (cleaned.startsWith('+254')) cleaned = cleaned.slice(1)
  else if (cleaned.length === 9) cleaned = '254' + cleaned
  else return 'Invalid Number'

  return cleaned.length === 12 ? cleaned : 'Invalid Number'
};

const proceedToPayment = async () => {
  if (!phoneNumber.value) return toast.error('Please enter phone number.')
  if (!props.facilityId) return toast.error('Facility Id is required to initiate payment.')

  try {
    promptingPayment.value = true
    const formattedPhoneNumber = formatKenyanPhoneNumber(phoneNumber.value)
    if (formattedPhoneNumber === 'Invalid Number') {
      promptingPayment.value = false;
      return toast.error('Please enter a valid Kenya Phone number')
    }

    const response = apiClient.initiatePaymentStkPush({
      amount: props.amount,
      facility_id: props.facilityId,
      phoneNumber: formattedPhoneNumber
    })

    if (response.success) {
      toast.success('Payment Initiated sucessfully. Please check your phone')
      setTimeout(() => {
        promptingPayment.value = false;
        initialPromptSent.value = true;
        phoneNumber.value = ''
      }, 3000)
    } else {
      toast.error(response.message || 'Failed to initiate payment')
      promptingPayment.value = false
    }

  } catch(e){
    promptingPayment.value = false;
    toast.error('Failed to initiate payment')
    console.log('Failed to initiate payment: ', e)
  }
};

const confirmPayment = async () => {
  if (!props.facilityId) return toast.error('Facility Id required to confirm payment')
  try {
    confirmingPayment.value = true
    const response = await apiClient.confirmBillPaymentStatus(props.facilityId)

    if (response.success) {
      toast.success('Payment processed successfully.')
      window.location.reload();
    } else {
      toast.error('Failed to complete payment')
    }

    confirmingPayment.value = false;
  } catch(e) {
    confirmingPayment.value = false;
    toast.error('Failed to process payment')
    console.log('Failed to process payment: ', e)
  }
}
</script>